import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Boxes,
  Check,
  Copy,
  Cpu,
  Database,
  GitBranch,
  KeyRound,
  Layers,
  Rocket,
  ShieldCheck,
  Sparkles,
  Workflow,
  Zap,
} from "lucide-react";
import { Aurora } from "@/components/motion/aurora";
import { FadeIn } from "@/components/motion/fade-in";
import { GridPattern } from "@/components/motion/grid-pattern";
import { Marquee } from "@/components/motion/marquee";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IntegrationLogo } from "@/components/integrations/logo";
import { cn } from "@/lib/utils";
import { FlowShowcase } from "@/components/site/flow-showcase";
import { AgentDeployShowcase } from "@/components/site/agent-deploy-showcase";
import { HighlightedCode } from "@/components/site/highlighted-code";
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  INTEGRATION_COUNT,
  INTEGRATIONS,
} from "@/data/integrations";

// Marquee slugs come straight from the live registry — every slug in
// the list has a logo at `/logos/v3/<slug>.svg`, so nothing falls back
// to the colored-letter placeholder.
const MARQUEE_SLUGS: string[] = INTEGRATIONS.slice(0, 36).map((i) => i.slug);

const FEATURES = [
  {
    icon: Boxes,
    title: "MCP Server Registry",
    desc: "Register every MCP server once. Expose them to Claude Desktop, Cursor, Windsurf, Open WebUI, and any custom client with a single URL.",
    viz: "mcp" as const,
  },
  {
    icon: Workflow,
    title: "Unified Tool Registry",
    desc: "Connect a tool once — your code asks for it as MCP, OpenAI Function, Anthropic Tool, LangChain Tool, Mastra Tool, or a REST endpoint.",
    viz: "tools" as const,
  },
  {
    icon: Database,
    title: "Memory + Vector layer",
    desc: "User memory, agent memory, project memory, semantic search — backed by Postgres and your choice of vector store. RAG-ready.",
    viz: "memory" as const,
  },
  {
    icon: KeyRound,
    title: "OAuth + API keys",
    desc: "Connect Google, GitHub, Slack, Notion, Microsoft, and 300+ providers once. We refresh tokens, scope permissions, and rotate keys.",
    viz: "oauth" as const,
  },
  {
    icon: ShieldCheck,
    title: "Fine-grained permissions",
    desc: "Decide what each agent and each client can do. Claude can read Gmail; Cursor can push to GitHub; Research agent sees docs only.",
    viz: "perms" as const,
  },
  {
    icon: Sparkles,
    title: "Agent runtime (coming soon)",
    desc: "Deploy long-running and scheduled agents on our edge. One-click deploys, scaling, monitoring, and rollbacks.",
    viz: "runtime" as const,
  },
];

type VizKind = "mcp" | "tools" | "memory" | "oauth" | "perms" | "runtime";

const CODE_SAMPLE = `// You wired Gmail, Drive, GitHub, Slack, Notion on memkernel.com.
// Your code never defines a tool — it just consumes them.

import { memkernel } from "@memkernel/sdk";
import Anthropic from "@anthropic-ai/sdk";

const mk = memkernel({ apiKey: process.env.MK_API_KEY! });
const anthropic = new Anthropic();

// Pick the project. Pick the shape your model expects.
// MemKernel returns every tool already exposed there — Gmail,
// Drive, GitHub, Slack, Notion — pre-shaped, scoped, and signed.
const tools = await mk.tools.export({
  project: "personal-assistant",
  format: "anthropic", // or "openai" | "mcp" | "langchain" | "mastra"
});

await anthropic.messages.create({
  model: "claude-sonnet-4-6",
  tools,
  messages: [{ role: "user", content: input }],
});`;

export function Home() {
  return (
    <div className="relative">
      <Hero />
      <LogosBand />
      <FlowDiagram />
      <AgentDeploySection />
      <FeatureGrid />
      <CodeShowcase />
      <CategoriesExplorer />
      <SDKBand />
      <CTASection />
    </div>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative isolate overflow-hidden pb-24 pt-20 sm:pt-28">
      <Aurora className="opacity-80" />
      <GridPattern className="opacity-30" />
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn immediate className="mx-auto flex max-w-fit">
          <a
            href="https://github.com/cemsina/memkernel"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-full border border-border/60 bg-background/60 px-4 py-1.5 text-xs font-medium backdrop-blur transition hover:border-foreground/30"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            Open source · Apache 2.0
            <span aria-hidden className="text-muted-foreground">→</span>
          </a>
        </FadeIn>

        <FadeIn immediate delay={0.05}>
          <h1 className="mx-auto mt-6 max-w-4xl text-center text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
            Connect every tool.
            <br className="hidden sm:inline" />{" "}
            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-teal-400 bg-clip-text text-transparent">
              Run any agent.
            </span>
          </h1>
        </FadeIn>

        <FadeIn immediate delay={0.1}>
          <p className="mx-auto mt-6 max-w-2xl text-balance text-center text-lg text-muted-foreground sm:text-xl">
            Connect tools, MCP servers, memory, and identity once — expose them
            to Claude, Cursor, ChatGPT, or any agent runtime through a unified
            interface.
          </p>
        </FadeIn>

        <FadeIn immediate delay={0.15}>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="group h-12 px-6 text-base">
              <Link to="/signup">
                Start free
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 px-6 text-base">
              <Link to="/docs/getting-started">Read the docs</Link>
            </Button>
          </div>
        </FadeIn>

        <FadeIn immediate delay={0.25}>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-muted-foreground">
            <span>
              <strong className="text-foreground">{INTEGRATION_COUNT}+</strong>{" "}
              integrations
            </span>
            <span className="hidden text-border sm:inline">·</span>
            <span>
              <strong className="text-foreground">MCP-native</strong>{" "}
              everywhere
            </span>
            <span className="hidden text-border sm:inline">·</span>
            <span>
              <strong className="text-foreground">TypeScript</strong>,{" "}
              <strong className="text-foreground">Python</strong>,{" "}
              <strong className="text-foreground">Go</strong> SDKs
            </span>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ── Logos marquee ────────────────────────────────────────────────────
function LogoTile({ slug, name }: { slug: string; name: string }) {
  return (
    <div
      className="flex h-12 w-12 shrink-0 items-center justify-center opacity-80 transition hover:scale-110 hover:opacity-100"
      title={name}
    >
      <IntegrationLogo slug={slug} name={name} size={40} />
    </div>
  );
}

function LogosBand() {
  // Three disjoint rows, each capped to the same length as row 1 so the
  // marquees all travel at the same visible speed (same content width →
  // same px/sec at the same animation duration).
  const bySlug = new Map(INTEGRATIONS.map((i) => [i.slug, i.name]));
  const seen = new Set<string>(MARQUEE_SLUGS);
  const remaining: { slug: string; name: string }[] = [];
  for (const i of INTEGRATIONS) {
    if (seen.has(i.slug)) continue;
    seen.add(i.slug);
    remaining.push({ slug: i.slug, name: i.name });
  }
  const perRow = MARQUEE_SLUGS.length;
  const row1 = MARQUEE_SLUGS.map((s) => ({ slug: s, name: bySlug.get(s) ?? s }));
  const row2 = remaining.slice(0, perRow);
  const row3 = remaining.slice(perRow, perRow * 2);
  return (
    <section className="border-y border-border/40 bg-muted/30 py-6">
      <div className="mx-auto max-w-7xl space-y-3 px-4 sm:px-6 lg:px-8">
        <Marquee duration={44} gapClassName="gap-6">
          {row1.map((i) => (
            <LogoTile key={"r1-" + i.slug} slug={i.slug} name={i.name} />
          ))}
        </Marquee>
        <Marquee duration={44} reverse gapClassName="gap-6">
          {row2.map((i) => (
            <LogoTile key={"r2-" + i.slug} slug={i.slug} name={i.name} />
          ))}
        </Marquee>
        <Marquee duration={44} gapClassName="gap-6">
          {row3.map((i) => (
            <LogoTile key={"r3-" + i.slug} slug={i.slug} name={i.name} />
          ))}
        </Marquee>
      </div>
    </section>
  );
}

// ── Connect-once flow diagram ────────────────────────────────────────
function FlowDiagram() {
  return (
    <section id="product" className="py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4">
            <Layers className="mr-1.5 h-3 w-3" />
            Connect once · use everywhere
          </Badge>
          <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            One control plane between every tool and every agent
          </h2>
          <p className="mt-4 text-muted-foreground sm:text-lg">
            Stop wiring the same OAuth, the same tool definitions, and the same
            permission checks into every framework. Configure once on
            MemKernel; route to anywhere.
          </p>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="mt-14">
            <FlowShowcase />
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ── Agent deploy showcase ────────────────────────────────────────────
function AgentDeploySection() {
  return (
    <section className="border-t border-border/40 py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4">
            <Sparkles className="mr-1.5 h-3 w-3" />
            Ship agents in seconds, not weeks
          </Badge>
          <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            Your stack is already wired. Deploys are instant.
          </h2>
          <p className="mt-4 text-muted-foreground sm:text-lg">
            Because your tools, memory, vectors, and identity already live on
            MemKernel, deploying a new agent is just <em>attaching</em> it to
            your project. No CI, no infra rewiring, no bespoke OAuth flow.
          </p>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="mt-14">
            <AgentDeployShowcase />
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ── Feature grid ─────────────────────────────────────────────────────
function FeatureGrid() {
  return (
    <section className="border-y border-border/40 bg-muted/20 py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4">
            <Cpu className="mr-1.5 h-3 w-3" />
            The MemKernel stack
          </Badge>
          <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            Everything an AI agent needs, in one control plane
          </h2>
          <p className="mt-4 text-muted-foreground sm:text-lg">
            We're not another chatbot or another framework. We're the layer
            underneath every agent — tools, memory, identity, permissions, and
            (soon) deployments.
          </p>
        </FadeIn>

        <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature, idx) => (
            <FadeIn key={feature.title} delay={idx * 0.05}>
              <div className="group relative h-full overflow-hidden rounded-2xl border border-border/60 bg-card p-6 transition-all hover:border-border hover:shadow-lg">
                <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-foreground/20 to-transparent" />
                <div className="relative mb-4 flex h-[110px] items-center justify-center overflow-hidden rounded-xl border border-border/40 bg-background/40">
                  <FeatureViz kind={feature.viz} />
                </div>
                <feature.icon className="h-6 w-6 text-foreground/70 transition-colors group-hover:text-foreground" />
                <h3 className="mt-3 text-lg font-semibold">{feature.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{feature.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Code showcase ────────────────────────────────────────────────────
function CodeShowcase() {
  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <FadeIn>
            <Badge variant="secondary" className="mb-4">
              <GitBranch className="mr-1.5 h-3 w-3" />
              Wire once · consume anywhere
            </Badge>
            <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
              Tools live on MemKernel. Your code just asks for them.
            </h2>
            <p className="mt-4 text-muted-foreground sm:text-lg">
              You connect Gmail, Drive, Slack, GitHub, Notion once on
              MemKernel — OAuth, scopes, refresh, the works. Then your
              agent asks for them in whatever shape your model expects.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Badge variant="outline">MCP Tool</Badge>
              <Badge variant="outline">OpenAI Function</Badge>
              <Badge variant="outline">Anthropic Tool</Badge>
              <Badge variant="outline">LangChain Tool</Badge>
              <Badge variant="outline">Mastra Tool</Badge>
              <Badge variant="outline">REST API</Badge>
            </div>
            <Button asChild className="mt-8">
              <Link to="/docs/sdks/typescript">Read the SDK docs →</Link>
            </Button>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="overflow-hidden rounded-2xl border border-border/60 bg-zinc-950 shadow-2xl">
              <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
                <span className="h-3 w-3 rounded-full bg-red-500/80" />
                <span className="h-3 w-3 rounded-full bg-yellow-500/80" />
                <span className="h-3 w-3 rounded-full bg-green-500/80" />
                <span className="ml-3 text-xs text-zinc-500">
                  agents/personal-assistant.ts
                </span>
              </div>
              <HighlightedCode code={CODE_SAMPLE} lang="ts" />
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

// ── Categories explorer ──────────────────────────────────────────────
function CategoriesExplorer() {
  const counts = CATEGORY_ORDER.map((key) => ({
    key,
    label: CATEGORY_LABELS[key],
    count: INTEGRATIONS.filter((i) => i.category === key).length,
  }));
  return (
    <section className="border-y border-border/40 bg-muted/20 py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4">
            <Zap className="mr-1.5 h-3 w-3" />
            {INTEGRATION_COUNT}+ integrations
          </Badge>
          <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            Connect what your agents actually reach for
          </h2>
          <p className="mt-4 text-muted-foreground sm:text-lg">
            From OAuth-grade SaaS to vector databases to self-hosted services
            — every category an agent touches, all in one catalog. New
            integrations land every week.
          </p>
        </FadeIn>

        <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
          {counts.map((c, idx) => (
            <FadeIn key={c.key} delay={idx * 0.02}>
              <Link
                to={`/integrations?category=${c.key}`}
                className="group flex items-center justify-between rounded-lg border border-border/40 bg-background px-3 py-2 text-sm transition hover:border-border hover:bg-card"
              >
                <span className="text-foreground/80 group-hover:text-foreground">
                  {c.label}
                </span>
                <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  {c.count}
                </span>
              </Link>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.2} className="mt-10 flex justify-center">
          <Button asChild variant="outline" size="lg">
            <Link to="/integrations">
              Browse all {INTEGRATION_COUNT}+ integrations
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </FadeIn>
      </div>
    </section>
  );
}

// ── SDKs band — tabbed switcher with live install + usage preview ───
const SDKS = [
  {
    slug: "typescript",
    name: "TypeScript",
    accent: "#3178C6",
    install: "pnpm add @memkernel/sdk",
    lang: "ts" as const,
    snippet: `import { memkernel } from "@memkernel/sdk";

const mk = memkernel({ apiKey: process.env.MK_API_KEY! });

const tools = await mk.tools.export({
  project: "personal-assistant",
  format: "anthropic",
});`,
    doc: "/docs/sdks/typescript",
  },
  {
    slug: "python",
    name: "Python",
    accent: "#FFD43B",
    install: "pip install memkernel",
    lang: "python" as const,
    snippet: `from memkernel import MemKernel

mk = MemKernel(api_key=os.environ["MK_API_KEY"])

tools = mk.tools.export(
    project="personal-assistant",
    format="anthropic",
)`,
    doc: "/docs/sdks/python",
  },
  {
    slug: "go",
    name: "Go",
    accent: "#00ADD8",
    install: "go get github.com/memkernel/sdk-go",
    lang: "go" as const,
    snippet: `import "github.com/memkernel/sdk-go"

mk := memkernel.New(memkernel.WithAPIKey(os.Getenv("MK_API_KEY")))

tools, err := mk.Tools.Export(ctx, memkernel.ExportOptions{
    Project: "personal-assistant",
    Format:  memkernel.FormatAnthropic,
})`,
    doc: "/docs/sdks/go",
  },
] as const;

function SDKBand() {
  const [active, setActive] = useState<(typeof SDKS)[number]["slug"]>("typescript");
  const [copied, setCopied] = useState(false);
  const sdk = SDKS.find((s) => s.slug === active) ?? SDKS[0]!;

  function onCopy() {
    void navigator.clipboard.writeText(sdk.install);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            Pick your stack
          </h2>
          <p className="mt-4 text-muted-foreground sm:text-lg">
            Official SDKs in the three languages you actually ship AI agents
            in. REST and MCP APIs everywhere else.
          </p>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="mx-auto mt-12 max-w-3xl">
            {/* Language tabs */}
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-card/60 p-1 backdrop-blur">
                {SDKS.map((s) => (
                  <button
                    key={s.slug}
                    type="button"
                    onClick={() => setActive(s.slug)}
                    className={
                      "relative rounded-full px-4 py-1.5 text-sm font-medium transition-colors " +
                      (active === s.slug
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground")
                    }
                  >
                    {active === s.slug && (
                      <motion.span
                        layoutId="sdk-pill"
                        className="absolute inset-0 -z-10 rounded-full"
                        style={{
                          background: `linear-gradient(135deg, ${s.accent}25, ${s.accent}10)`,
                          border: `1px solid ${s.accent}55`,
                        }}
                        transition={{ type: "spring", duration: 0.4, bounce: 0.25 }}
                      />
                    )}
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Install command */}
            <div
              className="mt-6 flex items-center gap-2 rounded-xl border border-border/60 bg-zinc-950 px-4 py-3 shadow-lg"
              style={{ boxShadow: `0 0 60px -30px ${sdk.accent}80` }}
            >
              <span className="text-xs text-zinc-500">$</span>
              <code className="flex-1 truncate font-mono text-sm text-zinc-100">
                {sdk.install}
              </code>
              <button
                type="button"
                onClick={onCopy}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                aria-label="Copy install command"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            </div>

            {/* Live code preview, syntax-highlighted via shiki */}
            <div className="mt-4 overflow-hidden rounded-2xl border border-border/60 bg-zinc-950 shadow-2xl">
              <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                  <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500/70" />
                </div>
                <span className="font-mono text-[11px] text-zinc-500">
                  {sdk.lang === "ts"
                    ? "main.ts"
                    : sdk.lang === "python"
                      ? "main.py"
                      : "main.go"}
                </span>
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                  style={{
                    background: `${sdk.accent}22`,
                    color: sdk.accent,
                    border: `1px solid ${sdk.accent}44`,
                  }}
                >
                  {sdk.name}
                </span>
              </div>
              <HighlightedCode code={sdk.snippet} lang={sdk.lang} />
            </div>

            <div className="mt-5 flex items-center justify-center">
              <Button asChild variant="outline">
                <Link to={sdk.doc}>
                  Read the {sdk.name} docs
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ── Per-card mini visualizations for the feature grid ───────────────
function FeatureViz({ kind }: { kind: VizKind }) {
  switch (kind) {
    case "mcp":     return <McpViz />;
    case "tools":   return <ToolsViz />;
    case "memory":  return <MemoryViz />;
    case "oauth":   return <OAuthViz />;
    case "perms":   return <PermsViz />;
    case "runtime": return <RuntimeViz />;
  }
}

function McpViz() {
  const reduce = useReducedMotion();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setCopied((c) => !c), 2200);
    return () => window.clearInterval(id);
  }, [reduce]);
  return (
    <div className="flex w-full flex-col items-center gap-2 px-4">
      <div className="flex w-full items-center gap-2 rounded-md border border-foreground/10 bg-background px-2.5 py-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
        <code className="flex-1 truncate font-mono text-[10px]">
          mcp.memkernel.com/p/personal
        </code>
        <motion.span
          animate={{ scale: copied ? [1, 1.2, 1] : 1 }}
          transition={{ duration: 0.3 }}
          className="text-foreground/60"
        >
          {copied ? <Check className="h-3 w-3 text-emerald-300" /> : <Copy className="h-3 w-3" />}
        </motion.span>
      </div>
      <div className="grid w-full grid-cols-3 gap-1">
        {["Claude", "Cursor", "Open WebUI"].map((c, i) => (
          <motion.div
            key={c}
            initial={false}
            animate={{ opacity: copied ? 1 : 0.4 }}
            transition={{ delay: i * 0.08, duration: 0.25 }}
            className="rounded border border-foreground/10 bg-background/60 px-1.5 py-1 text-center text-[9px]"
          >
            {c}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function ToolsViz() {
  const protocols = [
    { label: "MCP",       color: "#A855F7" },
    { label: "OpenAI fn", color: "#10A37F" },
    { label: "Anthropic", color: "#D97757" },
    { label: "LangChain", color: "#6366F1" },
    { label: "REST",      color: "#0EA5E9" },
  ];
  const reduce = useReducedMotion();
  return (
    <div className="flex w-full flex-col items-center gap-2 px-4">
      <div className="flex items-center gap-1.5 rounded-md border border-foreground/10 bg-background px-2 py-1">
        <span className="flex h-4 w-4 items-center justify-center rounded bg-white p-0.5 ring-1 ring-black/5">
          <IntegrationLogo slug="gmail" name="Gmail" size={12} />
        </span>
        <code className="font-mono text-[10px]">gmail.search</code>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-1">
        {protocols.map((p, i) => (
          <motion.span
            key={p.label}
            initial={reduce ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08, duration: 0.3 }}
            className="rounded-full border px-1.5 py-0.5 text-[9px] font-semibold"
            style={{ background: `${p.color}1f`, borderColor: `${p.color}55`, color: p.color }}
          >
            {p.label}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

function MemoryViz() {
  const reduce = useReducedMotion();
  const items = [
    { k: "user.tone",       v: "terse" },
    { k: "user.tz",         v: "Europe/Berlin" },
    { k: "thread.q4_plan",  v: "3 hits" },
  ];
  return (
    <div className="flex w-full flex-col items-center gap-1 px-4">
      {items.map((m, i) => (
        <motion.div
          key={m.k}
          initial={reduce ? false : { opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 + i * 0.12, duration: 0.3 }}
          className="flex w-full items-center gap-1.5 rounded border border-emerald-400/30 bg-emerald-400/10 px-2 py-1 text-[10px]"
        >
          <Sparkles className="h-2.5 w-2.5 shrink-0 text-emerald-300" />
          <span className="flex-1 truncate font-mono">{m.k}</span>
          <span className="rounded bg-foreground/10 px-1 py-0.5 text-[9px]">{m.v}</span>
        </motion.div>
      ))}
    </div>
  );
}

function OAuthViz() {
  const providers = [
    { slug: "google", name: "Google" },
    { slug: "github", name: "GitHub" },
    { slug: "slack",  name: "Slack"  },
    { slug: "notion", name: "Notion" },
  ];
  return (
    <div className="grid w-full grid-cols-4 gap-2 px-4">
      {providers.map((p, i) => (
        <motion.div
          key={p.slug}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.07 }}
          className="relative flex flex-col items-center gap-1 rounded-lg border border-foreground/10 bg-background/60 p-1.5"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded bg-white p-0.5 ring-1 ring-black/5">
            <IntegrationLogo slug={p.slug} name={p.name} size={16} />
          </span>
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
        </motion.div>
      ))}
    </div>
  );
}

function PermsViz() {
  const cells = [
    [true,  false, true ],
    [false, true,  true ],
  ];
  const reduce = useReducedMotion();
  return (
    <div className="flex w-full items-center justify-center px-4">
      <div className="grid grid-cols-4 gap-1.5 text-[9px] font-semibold">
        <div />
        {["Claude", "Cursor", "Research"].map((c) => (
          <div key={c} className="text-center text-muted-foreground">{c}</div>
        ))}
        {cells.map((row, ri) => (
          <div key={ri} className="contents">
            <div className="text-muted-foreground">
              {ri === 0 ? "Gmail" : "GitHub"}
            </div>
            {row.map((allowed, ci) => (
              <motion.div
                key={`${ri}-${ci}`}
                initial={reduce ? false : { scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 + (ri * 3 + ci) * 0.06 }}
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold",
                  allowed
                    ? "border border-emerald-400/40 bg-emerald-400/15 text-emerald-300"
                    : "border border-red-400/30 bg-red-400/10 text-red-300/80",
                )}
              >
                {allowed ? "✓" : "✗"}
              </motion.div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function RuntimeViz() {
  return (
    <div className="flex w-full flex-col items-center gap-2 px-4">
      <div className="relative flex h-10 w-10 items-center justify-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-indigo-500/30" />
        <span className="absolute inset-0 rounded-full border border-foreground/20" />
        <Rocket className="relative h-5 w-5 text-foreground" />
      </div>
      <div className="flex items-center gap-1">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="h-1 w-6 rounded-full bg-gradient-to-r from-indigo-400 to-fuchsia-400 opacity-70"
            style={{
              animation: `progress-pulse 1.6s ${i * 0.2}s ease-in-out infinite`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Closing CTA ──────────────────────────────────────────────────────
function CTASection() {
  return (
    <section className="relative isolate overflow-hidden py-24">
      <Aurora className="opacity-60" />
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <h2 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">
            Stop wiring the same{" "}
            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-teal-400 bg-clip-text text-transparent">
              integrations
            </span>{" "}
            into every agent.
          </h2>
          <p className="mt-6 text-balance text-lg text-muted-foreground sm:text-xl">
            One place to connect tools. One place to manage memory. One place
            to define what your agents can do.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="group h-12 px-6 text-base">
              <Link to="/signup">
                Start free
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 px-6 text-base">
              <Link to="/integrations">Browse integrations</Link>
            </Button>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
